import { Grid, Tabs, Tab, Box, Typography, Paper, Stack, Button, Chip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { alpha } from '@mui/material/styles';
import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PsiOpportunitiesList from '@/features/opportunities/list/PsiOpportunitiesList.tsx';
import { useGetMe } from '@/lib/api/me.ts';
import InvestorOpportunitiesList from '@/features/opportunities/list/InvestorOpportunitiesList.tsx';
import { useListOpportunities } from '@/lib/api/opportunities.ts';
import { OpportunityFilters, SuitabilityTestStatus } from '@inversor-hub/types';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import {
  ANALYTICS_PLATFORM,
  trackProductEventOncePerSession,
} from '@/lib/analytics/product-events';
import { useGetInvestorPreferences } from '@/lib/api/investor-preferences';
import QuickPreferencesModal from '@/features/investor/QuickPreferencesModal';
import { PageHeader } from '@/components/ui/PageHeader.tsx';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const OpportunitiesTabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`opportunities-tabpanel-${index}`}
    aria-labelledby={`opportunities-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const SuitabilityTestBanner = ({ status }: { status: SuitabilityTestStatus }) => {
  const navigate = useNavigate();

  if (status === 'PASSED') {
    return null;
  }

  const isFailed = status === 'FAILED';

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        mb: 3,
        p: { xs: 2, sm: 2.5 },
        borderRadius: '8px',
        borderColor: isFailed ? 'error.light' : 'warning.100',
        background: isFailed
          ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${theme.palette.background.paper} 72%)`
          : `linear-gradient(135deg, ${theme.palette.warning[50]} 0%, ${theme.palette.background.paper} 72%)`,
        boxShadow: `0 12px 28px ${alpha(theme.palette.secondary.main, 0.08)}`,
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: isFailed ? 'error.main' : 'warning.main',
        },
      })}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={(theme) => ({
              width: 44,
              height: 44,
              borderRadius: '8px',
              display: 'grid',
              placeItems: 'center',
              color: isFailed ? 'error.main' : 'warning.dark',
              backgroundColor: isFailed ? alpha(theme.palette.error.main, 0.1) : 'warning.50',
              flexShrink: 0,
            })}
          >
            <FactCheckOutlinedIcon />
          </Box>
          <Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ mb: 0.5 }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                {isFailed ? 'Test de idoneidad no superado' : 'Test de idoneidad pendiente'}
              </Typography>
              <Chip
                size="small"
                label={isFailed ? 'En pausa' : 'Pendiente'}
                color={isFailed ? 'error' : 'warning'}
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              {isFailed
                ? 'Necesitas esperar un tiempo antes de repetir el test. Mientras tanto puedes seguir explorando oportunidades.'
                : 'Completa el test para poder invertir y empezar colaboraciones con PSIs cuando encuentres una oportunidad interesante.'}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant={isFailed ? 'outlined' : 'contained'}
          color={isFailed ? 'inherit' : 'primary'}
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/test-idoneidad')}
          sx={{
            alignSelf: { xs: 'stretch', md: 'center' },
            whiteSpace: 'nowrap',
          }}
        >
          {isFailed ? 'Ver test' : 'Completar test'}
        </Button>
      </Stack>
    </Paper>
  );
};

const InvestorOpportunitiesSection = ({
  hasPassedSuitabilityTest,
  suitabilityTestStatus,
}: {
  hasPassedSuitabilityTest: boolean;
  suitabilityTestStatus: SuitabilityTestStatus;
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState<OpportunityFilters>({});
  const {
    isLoading: isLoadingPreferences,
    isError: isPreferencesError,
    data: investorPreferences,
  } = useGetInvestorPreferences();
  const [preferencesDismissed, setPreferencesDismissed] = useState(
    () => sessionStorage.getItem('preferences_prompt_dismissed') === 'true'
  );
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const hasCompletedPreferences = (investorPreferences?.desiredInvestmentTypes?.length ?? 0) > 0;
    if (
      !isLoadingPreferences &&
      !isPreferencesError &&
      !hasCompletedPreferences &&
      !preferencesDismissed
    ) {
      setModalOpen(true);
    }
  }, [
    investorPreferences?.desiredInvestmentTypes.length,
    isLoadingPreferences,
    isPreferencesError,
    preferencesDismissed,
  ]);

  const hasFilters =
    filters.province ||
    filters.city ||
    filters.rooms !== undefined ||
    filters.bathrooms !== undefined ||
    filters.type;
  const appliedFilters = hasFilters ? filters : undefined;

  const { data: allOpportunities, isLoading: isLoadingAll } =
    useListOpportunities<'investor'>(appliedFilters);
  const { data: interestedOpportunities, isLoading: isLoadingInterests } =
    useListOpportunities<'investor'>(
      appliedFilters ? { hasInterest: true, ...appliedFilters } : { hasInterest: true }
    );

  const tabs = useMemo(
    () => [
      {
        label: 'Explorar',
        subtitle: 'Todas las oportunidades disponibles',
        content: (
          <InvestorOpportunitiesList
            opportunities={allOpportunities}
            isLoading={isLoadingAll}
            showOnlyInterests={false}
            hasPassedSuitabilityTest={hasPassedSuitabilityTest}
            filters={filters}
            onFilterChange={setFilters}
          />
        ),
      },
      {
        label: `Mis intereses (${interestedOpportunities?.length ?? 0})`,
        subtitle: 'Oportunidades en las que mostraste interés',
        content: (
          <InvestorOpportunitiesList
            opportunities={interestedOpportunities}
            isLoading={isLoadingInterests}
            showOnlyInterests
            hasPassedSuitabilityTest={hasPassedSuitabilityTest}
            filters={filters}
            onFilterChange={setFilters}
          />
        ),
      },
    ],
    [
      allOpportunities,
      hasPassedSuitabilityTest,
      interestedOpportunities,
      isLoadingAll,
      isLoadingInterests,
      filters,
      setFilters,
    ]
  );

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const a11yProps = (index: number) => ({
    id: `opportunities-tab-${index}`,
    'aria-controls': `opportunities-tabpanel-${index}`,
  });

  return (
    <Box>
      <PageHeader title="Oportunidades" />
      <SuitabilityTestBanner status={suitabilityTestStatus} />
      <QuickPreferencesModal
        open={modalOpen}
        onComplete={() => setModalOpen(false)}
        onClose={() => {
          sessionStorage.setItem('preferences_prompt_dismissed', 'true');
          setPreferencesDismissed(true);
          setModalOpen(false);
        }}
      />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="opportunities tabs">
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} {...a11yProps(index)} />
          ))}
        </Tabs>
      </Box>
      {tabs.map((tab, index) => (
        <OpportunitiesTabPanel key={index} value={activeTab} index={index}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {tab.subtitle}
          </Typography>
          {tab.content}
        </OpportunitiesTabPanel>
      ))}
    </Box>
  );
};

const OpportunitiesPage = () => {
  const { data: user } = useGetMe();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (user?.role !== 'investor') {
      return;
    }

    trackProductEventOncePerSession(
      trackEvent,
      'opportunities_list_viewed',
      'opportunities_list_viewed',
      {
        role: 'investor',
        platform: ANALYTICS_PLATFORM,
      }
    );
  }, [trackEvent, user?.role]);

  if (!user) {
    return <div>Cargando...</div>;
  }

  if (user.role === 'psi') {
    return (
      <Grid container spacing={0}>
        <Grid item xs={12} lg={12}>
          <PsiOpportunitiesList />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={0}>
      <Grid item xs={12} lg={12}>
        <InvestorOpportunitiesSection
          hasPassedSuitabilityTest={user.suitabilityTestStatus === 'PASSED'}
          suitabilityTestStatus={user.suitabilityTestStatus}
        />
      </Grid>
    </Grid>
  );
};

export default OpportunitiesPage;
